import { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, ImagePlus, X, Loader2 } from "lucide-react";
import { useAuth, api, formatErr } from "../lib/auth";
import { AdminShell } from "../components/AdminShell";

const PRESETS = [
  { label: "Manifeste", v: { tag: "Manifeste", line1: "Une maison", line2: "d'idées pour les", line3: "marques désirables.", cta: "" } },
  { label: "Citation", v: { tag: "Citation", line1: "Le détail", line2: "", line3: "fait la différence.", cta: "" } },
  { label: "Nouveau projet", v: { tag: "Nouveau", line1: "Nouveau projet", line2: "en ligne.", line3: "Découvrez-le.", cta: "Lien en bio" } },
  { label: "Vide", v: { tag: "", line1: "", line2: "", line3: "", cta: "" } },
];

const DEFAULT = { format: "post", mono: true, footer_left: "", footer_right: "", ...PRESETS[0].v };

// Downscale on the client so uploads stay small regardless of the original photo size
const shrink = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, 2000 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => resolve(b), "image/jpeg", 0.88);
  };
  img.src = URL.createObjectURL(file);
});

const inputCls = "w-full bg-transparent border-b border-line py-3 text-base focus:outline-none focus:border-[var(--gold)] transition-colors duration-300";
const Field = ({ label, children }) => (
  <label className="block"><span className="font-mono text-[11px] tracking-[0.22em] uppercase text-stone">{label}</span>{children}</label>
);

export default function AdminVisuals() {
  const { user } = useAuth();
  const [spec, setSpec] = useState(DEFAULT);
  const [photo, setPhoto] = useState(null);
  const [photoName, setPhotoName] = useState("");
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const set = (k) => (e) => setSpec((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const render = async () => {
    const fd = new FormData();
    Object.entries(spec).forEach(([k, v]) => fd.append(k, v));
    if (photo) fd.append("photo", photo, "photo.jpg");
    setBusy(true);
    try {
      const { data } = await api.post("/visuals/render", fd, { responseType: "blob" });
      setPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(data); });
    } catch (e) {
      if (e.response?.status !== 422) toast.error(formatErr(e.response?.data?.detail) || "Erreur de rendu");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(render, 450);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec, photo, user]);

  if (user === null) return <div className="min-h-screen grid place-items-center text-stone">…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhoto(await shrink(f));
    setPhotoName(f.name);
  };

  return (
    <AdminShell active="visuals">
      <h1 className="font-display text-5xl mt-16" data-testid="visuals-title">Visuels <em className="text-gold">Instagram</em></h1>
      <p className="mt-4 text-stone max-w-xl">Composez un post ou une story dans la charte de la maison : votre texte, votre photo (optionnelle), puis téléchargez le PNG en pleine résolution.</p>
      <div className="mt-10 grid lg:grid-cols-12 gap-12">
        <form onSubmit={(e) => e.preventDefault()} className="lg:col-span-5 space-y-7" data-testid="visuals-form">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button key={p.label} type="button" data-testid={`visual-preset-${p.label}`} onClick={() => setSpec((s) => ({ ...s, ...p.v }))} className="rounded-full border border-line px-4 py-1.5 text-sm hover:border-[var(--gold)] hover:text-gold transition-colors duration-300">{p.label}</button>
            ))}
          </div>
          <div className="flex gap-2">
            {[["post", "Post 1080 × 1080"], ["story", "Story 1080 × 1920"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`visual-format-${v}`} onClick={() => setSpec((s) => ({ ...s, format: v }))} className={`flex-1 border px-4 py-3 text-sm transition-colors duration-300 ${spec.format === v ? "border-[var(--gold)] text-gold" : "border-line hover:border-stone"}`}>{l}</button>
            ))}
          </div>
          <Field label="Surtitre"><input data-testid="visual-tag" className={inputCls} value={spec.tag} onChange={set("tag")} maxLength={40} placeholder="Manifeste" /></Field>
          <Field label="Ligne 1"><input data-testid="visual-line1" className={inputCls} value={spec.line1} onChange={set("line1")} maxLength={60} /></Field>
          <Field label="Ligne 2"><input data-testid="visual-line2" className={inputCls} value={spec.line2} onChange={set("line2")} maxLength={60} /></Field>
          <Field label="Ligne 3 (italique or)"><input data-testid="visual-line3" className={`${inputCls} italic text-gold`} value={spec.line3} onChange={set("line3")} maxLength={60} /></Field>
          <Field label="Bouton (optionnel)"><input data-testid="visual-cta" className={inputCls} value={spec.cta} onChange={set("cta")} maxLength={40} placeholder="Lien en bio" /></Field>
          <div className="grid grid-cols-2 gap-6">
            <Field label="Pied gauche"><input data-testid="visual-footer-left" className={inputCls} value={spec.footer_left} onChange={set("footer_left")} maxLength={40} placeholder="Strategy · Creative · Media" /></Field>
            <Field label="Pied droit"><input data-testid="visual-footer-right" className={inputCls} value={spec.footer_right} onChange={set("footer_right")} maxLength={40} placeholder="Genève · Paris · Londres" /></Field>
          </div>
          <div className="border border-dashed border-line p-5">
            <div className="flex items-center justify-between gap-4">
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm hover:text-gold transition-colors duration-300">
                <ImagePlus size={18} /> {photo ? "Changer la photo" : "Ajouter une photo"}
                <input data-testid="visual-photo-input" type="file" accept="image/*" className="sr-only" onChange={onPhoto} />
              </label>
              {photo && (
                <button type="button" data-testid="visual-photo-remove" onClick={() => { setPhoto(null); setPhotoName(""); }} className="inline-flex items-center gap-1 text-xs text-stone hover:text-red-400 transition-colors duration-300"><X size={14} /> Retirer</button>
              )}
            </div>
            {photoName && <p className="mt-2 font-mono text-[11px] text-stone truncate" data-testid="visual-photo-name">{photoName}</p>}
            {photo && (
              <label className="mt-4 flex items-center gap-3 text-sm cursor-pointer">
                <input data-testid="visual-mono" type="checkbox" checked={spec.mono} onChange={set("mono")} className="accent-[var(--gold)]" /> Noir & blanc éditorial
              </label>
            )}
          </div>
        </form>
        <div className="lg:col-span-7">
          <div className={`relative mx-auto border border-line bg-ink-2 ${spec.format === "story" ? "aspect-[9/16] max-h-[720px]" : "aspect-square max-w-[560px]"}`} data-testid="visual-preview-wrap">
            {preview && <img src={preview} alt="Aperçu" className="w-full h-full object-contain" data-testid="visual-preview" />}
            {busy && <div className="absolute inset-0 grid place-items-center bg-black/30"><Loader2 className="animate-spin text-gold" /></div>}
            {!preview && !busy && <p className="absolute inset-0 grid place-items-center text-stone text-sm">Saisissez au moins une ligne de texte.</p>}
          </div>
          <div className="mt-6 flex items-center justify-between gap-4">
            <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-stone">{spec.format === "story" ? "1080 × 1920" : "1080 × 1080"} · PNG</p>
            <a href={preview || undefined} download={`allure-house-${spec.format}.png`} data-testid="visual-download-btn" aria-disabled={!preview} className={`inline-flex items-center gap-2 rounded-full bg-[var(--gold)] text-[var(--ink)] px-6 py-2.5 text-sm font-semibold hover:bg-[var(--gold-2)] transition-colors duration-300 ${!preview ? "pointer-events-none opacity-40" : ""}`}>
              <Download size={16} /> Télécharger
            </a>
          </div>
          <p className="mt-8 text-sm text-stone">Besoin des modèles prêts à l'emploi ? <Link to="/brand" className="link-underline text-[var(--bone)]">Kit de marque</Link></p>
        </div>
      </div>
    </AdminShell>
  );
}
